import React, { useRef, useContext, useState, useEffect } from "react";
import { gsap } from "gsap";

import { Route, useLocation, Routes } from "react-router-dom";

import Home from "./Home";

import Contact from "./Contact";
import PrivacyPolicy from "./PrivacyPolicy";
import Solutions from "./Solutions";
import AdminMenu from "./AdminMenu";

import CookieConsent from "./components/CookieConsent";
import Header from "./components/Header";
import SEO from "./components/SEO";

import { Link } from "react-router-dom";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
import { deviceType } from "detect-it";

import { GlobalContext } from "./index";

import { useEffectOnce } from "./hooks/useEffectOnce";
import { disableScroll, enableScroll } from "./hooks/preventScroll";
import useLocalStorage from "./hooks/useLocalStorage";
import useImage from "./hooks/useImage";
import { sendGAPageView, sendGACustomEvent } from "./hooks/googleAnalytics";

import caseData from "./data/cases/cases.json";
import welcomeVideoData from "./data/welcome-video/welcomeVideo.json";

import { useDebounce } from "./hooks/useDebounce";


const SITE_NAME =
	"Stunning Studio";

const DEFAULT_DESCRIPTION =
	"We craft tomorrow's social experiences and ads in real-time today. Based in Hamburg, Germany.";

const DEFAULT_IMAGE =
	"/img/seo/default.jpg";


const INDEXABLE_CASE_FILTERS =
	new Set([
		"work",
		"solution",
		"ai",
		"choiz-io",
		"fortnite",
		"unreal-engine",
	]);


const getMeaningfulString =
	(value) => {
		const result =
			String(
				value ??
					"",
			)
				.trim()
				.replace(
					/\s+/g,
					" ",
				);

		if (
			!result ||
			result ===
				"-" ||
			/^test\d*$/i.test(
				result,
			)
		) {
			return "";
		}

		return result;
	};


const parseBoolean =
	(value) => {
		if (
			typeof value ===
			"boolean"
		) {
			return value;
		}

		const normalized =
			String(
				value ??
					"",
			)
				.trim()
				.toLowerCase();

		if (
			!normalized
		) {
			return null;
		}

		if (
			[
				"true",
				"yes",
				"1",
				"index",
				"indexable",
			].includes(
				normalized,
			)
		) {
			return true;
		}

		if (
			[
				"false",
				"no",
				"0",
				"noindex",
				"non-indexable",
			].includes(
				normalized,
			)
		) {
			return false;
		}

		return null;
	};


const parseList =
	(value) => {
		if (
			Array.isArray(
				value,
			)
		) {
			return value
				.map(
					(item) =>
						getMeaningfulString(
							item,
						),
				)
				.filter(
					Boolean,
				);
		}

		const stringValue =
			getMeaningfulString(
				value,
			);

		if (
			!stringValue
		) {
			return [];
		}

		return stringValue
			.split(
				",",
			)
			.map(
				(item) =>
					getMeaningfulString(
						item,
					),
			)
			.filter(
				Boolean,
			);
	};


const uniqueStrings =
	(values) => {
		return [
			...new Set(
				values
					.map(
						(value) =>
							getMeaningfulString(
								value,
							),
					)
					.filter(
						Boolean,
					),
			),
		];
	};


const isCaseIndexable =
	(caseItem) => {
		const explicitValue =
			parseBoolean(
				caseItem?.[
					"seo-indexable"
				],
			);

		if (
			explicitValue !==
			null
		) {
			return explicitValue;
		}

		return caseItem?.[
			"full-access"
		] !==
			"full-access";
	};


const normalizeRequestPath =
	(value) => {
		let result =
			String(
				value ??
					"/",
			).trim();

		if (
			!result.startsWith(
				"/",
			)
		) {
			result =
				`/${result}`;
		}

		result =
			result.replace(
				/\/{2,}/g,
				"/",
			);

		if (
			result.length >
			1
		) {
			result =
				result.replace(
					/\/+$/g,
					"",
				);
		}

		return result ||
			"/";
	};


const normalizeCaseFilterValue =
	(value) => {
		const normalized =
			getMeaningfulString(
				value,
			)
				.toLowerCase();

		if (
			!normalized
		) {
			return "";
		}

		if (
			normalized ===
				"all" ||
			normalized ===
				"cases"
		) {
			return "all";
		}

		if (
			normalized ===
				"solution" ||
			normalized ===
				"solutions"
		) {
			return "solution";
		}

		if (
			normalized ===
				"choiz.io" ||
			normalized ===
				"choiz io" ||
			normalized ===
				"choiz-io"
		) {
			return "choiz-io";
		}

		return normalized
			.normalize(
				"NFKD",
			)
			.replace(
				/[\u0300-\u036f]/g,
				"",
			)
			.replace(
				/&/g,
				" and ",
			)
			.replace(
				/[^a-z0-9]+/g,
				"-",
			)
			.replace(
				/^-+|-+$/g,
				"",
			);
	};


const seoCasesByShorthand =
	new Map();


const caseGroups =
	new Map();


caseData.forEach(
	(caseItem) => {
		const shorthand =
			getMeaningfulString(
				caseItem
					?.shorthand,
			);

		if (
			!shorthand
		) {
			return;
		}

		if (
			!caseGroups.has(
				shorthand,
			)
		) {
			caseGroups.set(
				shorthand,
				[],
			);
		}

		caseGroups
			.get(
				shorthand,
			)
			.push(
				caseItem,
			);
	},
);


caseGroups.forEach(
	(
		group,
		shorthand,
	) => {
		seoCasesByShorthand.set(
			shorthand,
			group.find(
				(caseItem) =>
					isCaseIndexable(
						caseItem,
					),
			) ??
				group[0],
		);
	},
);


const getGeneratedCaseDescription =
	(caseItem) => {
		const name =
			getMeaningfulString(
				caseItem
					?.name,
			);

		const type =
			getMeaningfulString(
				caseItem
					?.type,
			)
				.toLowerCase();

		const focus =
			uniqueStrings([
				...parseList(
					caseItem
						?.technologies,
				),
				...parseList(
					caseItem
						?.filters,
				),
			])
				.slice(
					0,
					3,
				);

		const noun =
			type ===
				"solution"
				? "solution"
				: "project";

		if (
			focus.length
		) {
			return `${name} is a ${noun} by Stunning Studio focused on ${focus.join(
				", ",
			)}.`;
		}

		return `${name} is a ${noun} by Stunning Studio in Hamburg, Germany.`;
	};


const getCaseTitle =
	(caseItem) => {
		const seoTitle =
			getMeaningfulString(
				caseItem?.[
					"seo-title"
				],
			);

		if (
			seoTitle
		) {
			return seoTitle;
		}

		const name =
			getMeaningfulString(
				caseItem
					?.name,
			);

		return name
			? `${name} - ${SITE_NAME}`
			: SITE_NAME;
	};


const getCaseDescription =
	(caseItem) => {
		return (
			getMeaningfulString(
				caseItem?.[
					"seo-description"
				],
			) ||
			getMeaningfulString(
				caseItem
					?.summary,
			) ||
			getMeaningfulString(
				caseItem
					?.description,
			) ||
			getGeneratedCaseDescription(
				caseItem,
			)
		);
	};


const getCaseImage =
	(caseItem) => {
		const customImage =
			getMeaningfulString(
				caseItem?.[
					"seo-image"
				],
			);

		if (
			customImage
		) {
			return customImage;
		}

		const shorthand =
			getMeaningfulString(
				caseItem
					?.shorthand,
			);

		const poster =
			getMeaningfulString(
				caseItem?.[
					"poster-landscape-1600p"
				],
			);

		if (
			shorthand &&
			poster
		) {
			return `/img/cases/${shorthand}/poster/${poster}.jpg`;
		}

		if (
			shorthand
		) {
			return `/img/cases/${shorthand}/poster/${shorthand}-poster-landscape-1600p.jpg`;
		}

		return DEFAULT_IMAGE;
	};


const filterLabels =
	new Map([
		[
			"work",
			"Work",
		],
		[
			"solution",
			"Solutions",
		],
	]);


[
	...seoCasesByShorthand.values(),
]
	.filter(
		(caseItem) =>
			isCaseIndexable(
				caseItem,
			),
	)
	.forEach(
		(caseItem) => {
			parseList(
				caseItem
					?.filters,
			).forEach(
				(filter) => {
					const value =
						normalizeCaseFilterValue(
							filter,
						);

					if (
						!value ||
						value ===
							"all"
					) {
						return;
					}

					if (
						value ===
							"work"
					) {
						filterLabels.set(
							"work",
							"Work",
						);

						return;
					}

					if (
						value ===
							"solution"
					) {
						filterLabels.set(
							"solution",
							"Solutions",
						);

						return;
					}

					if (
						!filterLabels.has(
							value,
						)
					) {
						filterLabels.set(
							value,
							getMeaningfulString(
								filter,
							),
						);
					}
				},
			);
		},
	);


const getFilterOverviewPath =
	(normalizedFilter) => {
		if (
			normalizedFilter ===
			"all"
		) {
			return "/cases";
		}

		if (
			normalizedFilter ===
			"solution"
		) {
			return "/cases/solutions";
		}

		return `/cases/${normalizedFilter}`;
	};


const getCaseFilterValues =
	(caseItem) => {
		const values =
			new Set();

		const type =
			getMeaningfulString(
				caseItem
					?.type,
			);

		if (
			type
		) {
			const normalizedType =
				normalizeCaseFilterValue(
					type,
				);

			if (
				normalizedType
			) {
				values.add(
					normalizedType,
				);
			}
		}

		parseList(
			caseItem
				?.filters,
		).forEach(
			(filter) => {
				const value =
					normalizeCaseFilterValue(
						filter,
					);

				if (
					value &&
					value !==
						"all"
				) {
					values.add(
						value,
					);
				}
			},
		);

		return values;
	};


const buildCaseSEOInfo =
	({
		caseItem,
		locationPath,
		indexable,
	}) => {
		return {
			title:
				getCaseTitle(
					caseItem,
				),

			description:
				getCaseDescription(
					caseItem,
				),

			creator:
				"",

			type:
				getMeaningfulString(
					caseItem
						?.type,
				)
					.toLowerCase() ===
				"work"
					? "article"
					: "website",

			image:
				getCaseImage(
					caseItem,
				),

			url:
				locationPath,

			canonical:
				`/${caseItem.shorthand}`,

			indexable:
				indexable ??
				isCaseIndexable(
					caseItem,
				),

			follow:
				true,
		};
	};


const buildNotFoundSEOInfo =
	(locationPath) => {
		return {
			title:
				`Page Not Found - ${SITE_NAME}`,

			description:
				"The requested page does not exist.",

			creator:
				"",

			type:
				"website",

			image:
				DEFAULT_IMAGE,

			url:
				locationPath,

			canonical:
				null,

			indexable:
				false,

			follow:
				false,
		};
	};


const buildSEOInfoForLocation =
	(location) => {
		const locationPath =
			normalizeRequestPath(
				location?.pathname ||
					"/",
			);

		const pathParts =
			locationPath
				.split(
					"/",
				)
				.filter(
					Boolean,
				);

		if (
			locationPath ===
			"/"
		) {
			return {
				title:
					SITE_NAME,

				description:
					DEFAULT_DESCRIPTION,

				creator:
					"",

				type:
					"website",

				image:
					DEFAULT_IMAGE,

				url:
					"/",

				canonical:
					"/",

				indexable:
					true,

				follow:
					true,
			};
		}

		if (
			locationPath ===
			"/get-in-touch"
		) {
			return {
				title:
					`Get in Touch - ${SITE_NAME}`,

				description:
					"Get in touch with Stunning Studio in Hamburg, Germany to discuss real-time production, interactive experiences, branded games, virtual production and creative technology.",

				creator:
					"",

				type:
					"website",

				image:
					DEFAULT_IMAGE,

				url:
					locationPath,

				canonical:
					"/get-in-touch",

				indexable:
					true,

				follow:
					true,
			};
		}

		if (
			locationPath ===
			"/privacy"
		) {
			return {
				title:
					`Imprint & Privacy Policy - ${SITE_NAME}`,

				description:
					"Legal notice, imprint and privacy information for Stunning Studio.",

				creator:
					"",

				type:
					"website",

				image:
					DEFAULT_IMAGE,

				url:
					locationPath,

				canonical:
					"/privacy",

				indexable:
					true,

				follow:
					true,
			};
		}

		if (
			locationPath ===
			"/admin"
		) {
			return {
				title:
					`Admin - ${SITE_NAME}`,

				description:
					"Stunning Studio administration.",

				creator:
					"",

				type:
					"website",

				image:
					DEFAULT_IMAGE,

				url:
					locationPath,

				canonical:
					null,

				indexable:
					false,

				follow:
					false,
			};
		}

		if (
			locationPath ===
			"/welcome"
		) {
			return {
				title:
					SITE_NAME,

				description:
					DEFAULT_DESCRIPTION,

				creator:
					"",

				type:
					"website",

				image:
					DEFAULT_IMAGE,

				url:
					locationPath,

				canonical:
					null,

				indexable:
					false,

				follow:
					false,
			};
		}

		if (
			pathParts.length ===
				1 &&
			locationPath.startsWith(
				"/welcome-",
			)
		) {
			const welcomeItem =
				welcomeVideoData.find(
					(item) =>
						item.shorthand ===
						pathParts[0],
				);

			if (
				!welcomeItem
			) {
				return buildNotFoundSEOInfo(
					locationPath,
				);
			}

			const rawName =
				getMeaningfulString(
					welcomeItem
						?.name,
				);

			const personName =
				rawName
					? rawName[0].toUpperCase() +
					  rawName.slice(
							1,
					  )
					: "";

			const poster =
				getMeaningfulString(
					welcomeItem?.[
						"poster-landscape-1600p"
					],
				);

			return {
				title:
					personName
						? `Hey ${personName}, nice to meet you. - ${SITE_NAME}`
						: SITE_NAME,

				description:
					personName
						? `Hey ${personName}, nice to meet you.`
						: DEFAULT_DESCRIPTION,

				creator:
					"",

				type:
					"website",

				image:
					poster
						? `/img/cases/welcome-video/poster/${poster}.jpg`
						: DEFAULT_IMAGE,

				url:
					locationPath,

				canonical:
					null,

				indexable:
					false,

				follow:
					false,
			};
		}

		if (
			locationPath ===
			"/cases"
		) {
			return {
				title:
					`Cases - ${SITE_NAME}`,

				description:
					"Explore selected work and solutions by Stunning Studio across real-time production, interactive experiences, Unreal Engine, Fortnite, AI and emerging technologies.",

				creator:
					"",

				type:
					"website",

				image:
					DEFAULT_IMAGE,

				url:
					locationPath,

				canonical:
					"/cases",

				indexable:
					true,

				follow:
					true,
			};
		}

		if (
			pathParts.length ===
				2 &&
			pathParts[0] ===
				"showcase"
		) {
			const caseItem =
				seoCasesByShorthand.get(
					pathParts[1],
				);

			if (
				!caseItem
			) {
				return buildNotFoundSEOInfo(
					locationPath,
				);
			}

			return buildCaseSEOInfo({
				caseItem,
				locationPath,
				indexable:
					false,
			});
		}

		if (
			pathParts[0] ===
			"cases"
		) {
			if (
				pathParts.length ===
				2
			) {
				const secondPart =
					pathParts[1];

				const requestedFilter =
					normalizeCaseFilterValue(
						secondPart,
					);

				const isKnownFilter =
					Boolean(
						requestedFilter &&
							(
								requestedFilter ===
									"all" ||
								filterLabels.has(
									requestedFilter,
								)
							),
					);

				if (
					isKnownFilter
				) {
					const label =
						requestedFilter ===
							"all"
							? "Cases"
							: filterLabels.get(
									requestedFilter,
							  ) ||
							  "Cases";

					return {
						title:
							`${label} - ${SITE_NAME}`,

						description:
							`${label} projects and solutions by Stunning Studio, a creative studio in Hamburg specializing in real-time production, interactive experiences and emerging technologies.`,

						creator:
							"",

						type:
							"website",

						image:
							DEFAULT_IMAGE,

						url:
							locationPath,

						canonical:
							getFilterOverviewPath(
								requestedFilter,
							),

						indexable:
							requestedFilter !==
								"all" &&
							INDEXABLE_CASE_FILTERS.has(
								requestedFilter,
							),

						follow:
							true,
					};
				}

				const caseItem =
					seoCasesByShorthand.get(
						secondPart,
					);

				if (
					caseItem
				) {
					return buildCaseSEOInfo({
						caseItem,
						locationPath,
					});
				}

				return buildNotFoundSEOInfo(
					locationPath,
				);
			}

			if (
				pathParts.length ===
				3
			) {
				const requestedFilter =
					normalizeCaseFilterValue(
						pathParts[1],
					);

				const isKnownFilter =
					Boolean(
						requestedFilter &&
							(
								requestedFilter ===
									"all" ||
								filterLabels.has(
									requestedFilter,
								)
							),
					);

				const caseItem =
					seoCasesByShorthand.get(
						pathParts[2],
					);

				if (
					isKnownFilter &&
					caseItem
				) {
					const belongsToFilter =
						requestedFilter ===
							"all" ||
						getCaseFilterValues(
							caseItem,
						).has(
							requestedFilter,
						);

					if (
						belongsToFilter
					) {
						return buildCaseSEOInfo({
							caseItem,
							locationPath,
						});
					}
				}

				return buildNotFoundSEOInfo(
					locationPath,
				);
			}

			return buildNotFoundSEOInfo(
				locationPath,
			);
		}

		if (
			pathParts.length ===
			1
		) {
			const caseItem =
				seoCasesByShorthand.get(
					pathParts[0],
				);

			if (
				caseItem
			) {
				return buildCaseSEOInfo({
					caseItem,
					locationPath,
				});
			}
		}

		return buildNotFoundSEOInfo(
			locationPath,
		);
	};


gsap.registerPlugin(DrawSVGPlugin);

gsap.config({
	// autoSleep: 60,
	force3D: true,
	nullTargetWarn: true,
	trialWarn: false,
	// units: { left: "%", top: "%", rotation: "rad" },
});

const App = React.forwardRef((props, ref) => {
	let location = useLocation();

	const solutionsRef = useRef(null);

	const prevRoute = useRef(["", Date.now()]);
	const [keySuffix, setKeySuffix] = useState(Date.now());

	const routes = [
		{ path: "/admin", name: "Config", Component: AdminMenu },
		{
			path: "/get-in-touch",
			name: "Get in Touch",
			Component: Contact,
		},
		{
			path: "/privacy",
			name: "Privacy Policy",
			Component: PrivacyPolicy,
		},
		{
			path: "/cases/*",
			name: "Cases",
			Component: Solutions,
			ref: solutionsRef,
			key: "/cases",
		},

		// /kickz-mooncourt
		...caseData.map((entry) => {
			return {
				path: `/${entry.shorthand}`,
				name: `${entry.name}`,
				Component: Solutions,
				ref: solutionsRef,
				key: "/cases",
			};
		}),

		// /showcase/kickz-mooncourt
		...caseData.map((entry) => {
			return {
				path: `/showcase/${entry.shorthand}`,
				name: `${entry.name}`,
				Component: Solutions,
				ref: solutionsRef,
				key: "/cases",
			};
		}),

		{ path: "/*", name: "Landing", Component: Home },
	];

	const [globalContext, setGlobalContext] = useContext(GlobalContext);

	const [windowBounds, setWindowBounds] = useState({
		ww: -1,
		wh: -1,
	});

	const [appView, setAppView] = useState(null);

	const circleRef = useRef(null);
	const headerRef = useRef(null);

	// const asideRef = useRef();
	// const footerRef = useRef();

	const initLoad = useRef(false);
	const initLoadCallback = useRef(null);

	const circleRefSVG = useRef();
	const mousePos = useRef({ x: 0, y: 0 });

	/*
	 * Keeps track of the cursor mode without changing
	 * the existing behavior of active / idle / none / load.
	 *
	 * "hidden" is the new special state used by VideoModal.
	 */
	const cursorState = useRef("idle");

	const [resizeTime, setResizeTime] = useState(Date.now());
	const debResizeTime = useDebounce(resizeTime, 500);

	const [fullAccess, setFullAccess] = useLocalStorage(
		"full-access",
		true,
	);

	const SEOInfo =
		buildSEOInfoForLocation(
			location,
		);

	useEffect(() => {
		if (location.pathname == "/welcome") {
			setFullAccess(true);
		} else if (
			location.pathname.startsWith("/welcome-") ||
			location.pathname.includes("/showcase/")
		) {
			//Change in Solutions.js aswell
			setFullAccess(true);

			// disableScroll();
			// initLoadCallback.current = () => {
			//   setTimeout(() => {
			//     window.scrollTo({
			//       top: -500,
			//       behavior: "smooth",
			//     });
			//     document
			//       .querySelector("#welcome-video")
			//       .scrollIntoView({
			//         behavior: "smooth",
			//         block: "center"
			//       });
			//     enableScroll();
			//   }, 3000);
			// };
		}

		//sendGAPageView(globalContext, location.pathname);

		sendGACustomEvent(
			globalContext,
			"p",
			location.pathname,
		);
	}, [location]);

	useEffectOnce(() => {
		console.log("mount app");

		const setPosition = (e) => {
			const xPosition = e.clientX;
			const yPosition = e.clientY;

			mousePos.current = {
				x: xPosition,
				y: yPosition,
			};
		};

		window.addEventListener(
			"mousemove",
			setPosition,
		);

		window.addEventListener(
			"resize",
			onResize,
		);

		onResize();

		gsap.set(circleRef.current, {
			opacity: 0,
		});

		// Every tick
		gsap.ticker.add(updatePosition);

		gsap.from(circleRefSVG.current, {
			stroke: "rgba(255, 255, 255,0)",
			drawSVG: "0%",
			duration: 2,
			overwrite: true,
			delay: 0.5,
		});

		// fix for preview browsers e.g. instagram
		setTimeout(() => {
			onResize();
		}, 300);

		setGlobalContext({
			// window: windowBounds,
			// initLoad: initLoading,
			changeCursorState: changeCursorState,
			// changeHeaderState: changeHeaderState,
			// changeView: changeView,
			// appView: appView,
			// deviceType: deviceType,
		});

		console.log(globalContext);

		return () => {
			window.removeEventListener(
				"mousemove",
				setPosition,
			);

			window.removeEventListener(
				"resize",
				onResize,
			);

			gsap.ticker.remove(updatePosition);

			console.log("unmount app");
		};
	});

	const initLoading = () => {
		if (!initLoad.current) {
			console.log("loooad");

			initLoad.current = true;

			initLoadCallback.current?.();
		}
	};

	const changeCursorState = (state) => {
		/*
		 * Store the state, but otherwise preserve the
		 * original behavior for all existing states.
		 */
		cursorState.current = state;

		gsap.killTweensOf(circleRefSVG.current);

		switch (state) {
			case "active":
				gsap.to(circleRefSVG.current, {
					stroke: "rgba(241, 3, 136,1)",
					drawSVG: "100% 0%",
					duration: 0.25,
					overwrite: true,
				});

				break;

			case "idle":
				gsap.to(circleRefSVG.current, {
					stroke: "rgba(255, 255, 255,1)",
					drawSVG: "100% 0%",
					duration: 0.25,
					overwrite: true,
				});

				break;

			case "none":
				/*
				 * ORIGINAL behavior.
				 *
				 * Do not change this because other parts
				 * of the site already rely on it.
				 */
				gsap.to(circleRefSVG.current, {
					stroke: "rgba(255, 255, 255,1)",
					drawSVG: "0% 0%",
					duration: 0.25,
					overwrite: true,
				});

				break;

			case "hidden":
				/*
				 * NEW behavior.
				 *
				 * Completely hide the custom cursor wrapper.
				 * Used specifically by VideoModal.
				 */
				gsap.killTweensOf(circleRef.current);

				gsap.set(circleRef.current, {
					opacity: 0,
				});

				break;

			case "load":
				gsap.to(circleRefSVG.current, {
					stroke: "rgba(241, 3, 136,1)",
					drawSVG: "0% 0%",
					duration: 0.15,
					overwrite: true,

					onComplete: () => {
						gsap.to(circleRefSVG.current, {
							duration: 1.75,
							ease: "ease.out",
							drawSVG: "100% 0%",

							onComplete: () => {
								gsap.to(
									circleRefSVG.current,
									{
										stroke:
											"rgba(255, 255, 255,1)",
										duration: 2,
										ease: "back.out(4)",
									},
								);
							},
						});
					},
				});

				break;

			default:
				break;
		}
	};

	const changeHeaderState = (
		state,
		data = null,
	) => {
		headerRef.current?.changeHeaderState?.(
			state,
			data,
		);
	};

	const closeSearch = () => {
		headerRef.current?.closeSearch?.();
	};

	const isSearchOpen = () => {
		return headerRef.current?.isSearchOpen?.();
	};

	const setFilter = (filter) => {
		solutionsRef.current?.setFilter?.(filter);
	};

	const changeView = (view) => {
		// console.log("NEW VIEW", view);
		setAppView(view);
	};

	const updatePosition = () => {
		if (
			mousePos.current.x === 0 &&
			mousePos.current.y === 0
		) {
			return;
		}

		/*
		 * NEW special case.
		 *
		 * While hidden, continue updating the position so
		 * that when the cursor becomes visible again it is
		 * already at the correct mouse position.
		 *
		 * But do NOT allow the original opacity: 1 behavior
		 * to make it visible.
		 */
		if (cursorState.current === "hidden") {
			gsap.set(circleRef.current, {
				x: mousePos.current.x - 20,
				y: mousePos.current.y - 20,
				opacity: 0,
			});

			return;
		}

		/*
		 * ORIGINAL site-wide behavior.
		 */
		gsap.to(circleRef.current, {
			x: mousePos.current.x - 20,
			y: mousePos.current.y - 20,
			opacity: 1,
			duration: 0.3,
		});
	};

	const onResize = () => {
		setResizeTime(Date.now());
		return;
	};

	useEffect(() => {
		let ih = window.innerHeight * 0.01;

		//set ih to have innerHeight for css calculation
		document.documentElement.style.setProperty(
			"--ih",
			`${ih}px`,
		);

		const ww = window.innerWidth;
		const wh = window.innerHeight;

		setWindowBounds({
			ww: ww,
			wh: wh,
		});
	}, [debResizeTime]);

	if (
		(prevRoute.current?.[0].startsWith("/cases") &&
			!location.pathname.startsWith("/cases")) ||
		(!prevRoute.current?.[0].startsWith("/cases") &&
			location.pathname.startsWith("/cases"))
	) {
		prevRoute.current[0] = location.pathname;
		prevRoute.current[1] = Date.now();
	}

	useEffect(() => {
		//Handle Back Button
		window.onpopstate = () => {
			setKeySuffix(Date.now());
		};
	});

	return (
		<div className="body">
			<SEO
				title={SEOInfo.title}
				description={SEOInfo.description}
				creator={SEOInfo.creator}
				type={SEOInfo.type}
				image={SEOInfo.image}
				url={SEOInfo.url}
				canonical={SEOInfo.canonical}
				indexable={SEOInfo.indexable}
				follow={SEOInfo.follow}
			/>

			{/*
			<div
				className="footer"
				style={{ opacity: 0 }}
				ref={footerRef}
			>
				<Link
					to="/privacy"
					className={
						location.pathname === "/privacy"
							? "disabled-link"
							: "cookie"
					}
				>
					Legal Notice
				</Link>
			</div>
			*/}

			<div
				className="progress-wrap"
				style={{
					display:
						deviceType === "mouseOnly"
							? "block"
							: "none",
				}}
				ref={circleRef}
			>
				<svg
					className="progress-circle svg-content"
					width="100%"
					height="100%"
					viewBox="-8 -8 116 116"
				>
					<path
						ref={circleRefSVG}
						d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
					/>
				</svg>
			</div>

			<Header
				ref={headerRef}
				setFilter={setFilter}
			/>

			<Routes>
				{routes.map(
					({
						path,
						Component,
						key,
						ref,
					}) => (
						<Route
							key={path}
							path={path}
							element={
								<Component
									globalContext={
										globalContext
									}
									key={`${
										key ?? path
									}_${keySuffix}`}
									cKey={`${
										key ?? path
									}_${keySuffix}`}
									currentPathname={
										location.pathname
									}
									window={
										windowBounds
									}
									initLoad={
										initLoading
									}
									changeCursorState={
										changeCursorState
									}
									changeHeaderState={
										changeHeaderState
									}
									closeSearch={
										closeSearch
									}
									isSearchOpen={
										isSearchOpen
									}
									changeView={
										changeView
									}
									appView={
										appView
									}
									deviceType={
										deviceType
									}
									ref={ref}
								/>
							}
						/>
					),
				)}
			</Routes>

			<CookieConsent debug={false} />
		</div>
	);
});

export default App;